import {  Typography, Box, List, ListItem, ListItemIcon, ListItemText, Paper } from '@mui/material';
import { LucideIcon, CheckCircle2, ArrowRight } from 'lucide-react';

interface StageService {
  icon: LucideIcon;
  stage: string;
  title: string;
  subtitle: string;
  duration: string;
  pricing: string;
  keyFeatures: string[];
  href: string;
  stageColor: string;
  stageNumber: number;
}

interface StageComparisonFrameProps {
  services: StageService[];
  onServiceClick: (href: string) => void;
}

export function StageComparisonFrame({ services, onServiceClick }: StageComparisonFrameProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: '24px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.default',
          px: { xs: 3, md: 6 },
          py: 3,
          borderBottom: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Your Growth Journey
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Click any stage to learn more about our tailored services
        </Typography>
      </Box>

      {/* Cards Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 0,
        }}
      >
        {services.map((service, index) => {
          const Icon = service.icon;
          const isLast = index === services.length - 1;

          return (
            <Box
              key={service.stage}
              sx={{
                flex: 1,
                position: 'relative',
                borderRight: { xs: 'none', md: !isLast ? '2px solid' : 'none' },
                borderBottom: { xs: !isLast ? '2px solid' : 'none', md: 'none' },
                borderColor: 'divider',
              }}
            >
              <Box
                onClick={() => onServiceClick(service.href)}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: `${service.stageColor}08`,
                    '& .stage-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                    '& .arrow-icon': {
                      transform: 'translateX(4px)',
                    },
                  },
                }}
              >
                {/* Stage Badge */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    bgcolor: service.stageColor,
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    mb: 3,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                    STAGE {service.stageNumber}
                  </Typography>
                </Box>

                {/* Icon */}
                <Box
                  className="stage-icon"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '18px',
                    bgcolor: `${service.stageColor}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    border: `3px solid ${service.stageColor}30`,
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <Icon size={36} color={service.stageColor} strokeWidth={2} />
                </Box>

                {/* Title Section */}
                <Typography
                  variant="overline"
                  sx={{
                    color: service.stageColor,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {service.stage}
                </Typography>
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  {service.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                  {service.subtitle}
                </Typography>

                {/* Divider */}
                <Box
                  sx={{
                    height: 3,
                    bgcolor: `${service.stageColor}25`,
                    borderRadius: 2,
                    mb: 3,
                  }}
                />

                {/* Key Features */}
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  What&apos;s Included
                </Typography>
                <List disablePadding sx={{ mb: 3 }}>
                  {service.keyFeatures.map((feature, featureIndex) => (
                    <ListItem key={featureIndex} disableGutters sx={{ py: 0.75, px: 0, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                        <CheckCircle2 size={16} color={service.stageColor} strokeWidth={2.5} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { lineHeight: 1.7, fontSize: '0.875rem' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Learn More Link */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: service.stageColor,
                    fontWeight: 600,
                    mt: 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Learn More
                  </Typography>
                  <ArrowRight
                    className="arrow-icon"
                    size={18}
                    strokeWidth={3}
                    style={{ transition: 'transform 0.3s ease' }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}